import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiRefreshCw, FiUnlock, FiRotateCcw } from "react-icons/fi";
import { usePermission } from "../../context/PermissionContext";
import {
  fetchPhoneUser,
  formatDuration,
  providerLabel,
  resetPhoneUser,
  unblockPhoneUser,
} from "../../utils/smsVerificationApi";

/**
 * One user's phone verification, for support: the verified number, attempt
 * counters / block, the last codes (never the codes themselves), and the two
 * support actions — unblock, and reset (unlinks the number, puts KYC step 4
 * back). Shown in the SMS Verification page and on the user's KYC tab.
 */

const STATUS_BADGE = {
  pending: "bg-blue-50 text-blue-700 border-blue-200",
  verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-gray-100 text-gray-600 border-gray-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const PhoneUserPanel = ({ userId, onChanged }) => {
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("sms-verification", "edit");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchPhoneUser(userId);
      setData(res.data);
    } catch (err) {
      toast.error(err.message || "Could not load phone verification");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, message) => {
    setBusy(true);
    try {
      const res = await fn();
      setData(res.data);
      toast.success(message);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const unblock = () => act(() => unblockPhoneUser(userId, { resetBlockCount: true }), "User unblocked");
  const reset = () => {
    if (!window.confirm("Reset phone verification? The verified number is unlinked (any account can use it again) and KYC step 4 goes back to pending.")) return;
    act(() => resetPhoneUser(userId), "Phone verification reset");
  };

  if (loading && !data) return <div className="text-xs text-gray-400 py-4">Loading phone verification…</div>;
  if (!data) return null;

  const { state, verifiedPhone, kyc, challenges } = data;
  const blocked = state.review || state.blocked;

  return (
    <div className="space-y-3" data-testid="phone-user-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 text-xs">
          <div>
            <span className="text-gray-400">Verified number: </span>
            <span className="font-semibold text-gray-800">{verifiedPhone ? verifiedPhone.phoneNumber : "—"}</span>
            {verifiedPhone && <span className="text-gray-400"> · {formatDate(verifiedPhone.verifiedAt)}</span>}
          </div>
          <div>
            <span className="text-gray-400">KYC step 4: </span>
            <span className="font-medium text-gray-700">{kyc ? kyc.step4Status : "not started"}</span>
          </div>
          <div>
            <span className="text-gray-400">Failed tries: </span>
            <span className="font-medium text-gray-700">{state.failedCount}</span>
            <span className="text-gray-400"> · Blocks so far: </span>
            <span className="font-medium text-gray-700">{state.blockCount}</span>
          </div>
          {state.review && <div className="text-red-600 font-semibold">Locked — needs admin review</div>}
          {!state.review && state.blocked && (
            <div className="text-amber-600 font-semibold">Blocked for {formatDuration(state.retryAfterSeconds)}</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50" aria-label="Refresh phone verification">
            <FiRefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          {canEdit && blocked && (
            <button onClick={unblock} disabled={busy} className="px-3 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl flex items-center gap-1.5 disabled:opacity-40">
              <FiUnlock size={12} /> Unblock
            </button>
          )}
          {canEdit && (verifiedPhone || (kyc && kyc.step4Status === "completed") || challenges.length > 0) && (
            <button onClick={reset} disabled={busy} className="px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl flex items-center gap-1.5 disabled:opacity-40">
              <FiRotateCcw size={12} /> Reset
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-100 rounded-xl">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Requested</th>
              <th className="text-left px-3 py-2">User's number</th>
              <th className="text-left px-3 py-2">Sent to</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {challenges.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-400">No codes requested yet.</td>
              </tr>
            )}
            {challenges.map((c) => (
              <tr key={c.id}>
                <td className="px-3 py-2 text-gray-600">{formatDate(c.createdAt)}</td>
                <td className="px-3 py-2 font-medium text-gray-800">{c.phoneNumber}</td>
                <td className="px-3 py-2 text-gray-600">
                  {c.destinationNumber} <span className="text-gray-400">({providerLabel(c.provider)})</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${STATUS_BADGE[c.status] || ""}`}>{c.status}</span>
                  {c.failureReason && <span className="text-gray-400 ml-1">{c.failureReason.replace(/_/g, " ")}</span>}
                </td>
                <td className="px-3 py-2 text-gray-400 hidden md:table-cell">{c.requestIp || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PhoneUserPanel;
